/**
 * Mirrors apps/api/src/membership/membership-admin.controller.ts
 * (t/:slug/api/v1/{memberships,invitations,roles,principals}) — verified
 * against the real wire contract in
 * apps/api/test/integration/membership-admin-http.integration.test.ts and
 * packages/auth/src/membership-admin/presentation.ts's
 * `toMembershipIdentityPresentation`.
 *
 * The list endpoint never returns a raw `membershipId`/`accountId` — only a
 * signed, tenant-scoped, opaque `memberRef` (never a raw UUID in customer
 * UI). `disable`/`reactivate` take that same `memberRef` as their path
 * param (`POST .../memberships/:memberRef/disable`), and so does role
 * change: `POST .../memberships/:memberRef/role` (`{ roleId,
 * confirmTenantAdmin? }`) resolves the membership's real `accountId`
 * server-side and replaces its active role assignment(s) with exactly one
 * new role — no raw `principalId` needed client-side. `assignRole`/
 * `unassignRole` (`POST/DELETE .../principals/:principalId/roles/:roleId`)
 * still exist and still take a raw `principalId`, but `UsersPage.tsx` no
 * longer uses them for its row-level role dropdown now that the
 * memberRef-scoped endpoint covers the same case for every member, not
 * only ones invited in the current browser session.
 */
/** Mirrors `TenantInvitationStatus` in packages/auth/src/membership-admin/presentation.ts. */
export type InviteStatus =
  'pending' | 'sms_sent' | 'sms_failed' | 'accepted' | 'expired' | 'cancelled';

export interface MembershipSummary {
  readonly memberRef: string;
  readonly membershipStatus: string;
  readonly roleDisplayNames: readonly string[];
  /** Present only when Auth/Profile already stores a customer-visible name. */
  readonly displayName?: string;
  /** Present only when Auth/Profile already stores a displayable login identifier. */
  readonly loginIdentifier?: string;
  /** Present only when this membership has ever had an invitation. */
  readonly inviteStatus?: InviteStatus;
  readonly accessReviewRequired?: boolean;
}

/** `GET permissions/catalog` — mirrors `PermissionCatalogGroup` in packages/auth. */
export interface PermissionCatalogGroup {
  readonly id: string;
  readonly label: string;
  readonly keys: readonly string[];
}

export interface PermissionCatalogResponse {
  readonly groups: readonly PermissionCatalogGroup[];
}

export interface CustomRoleCreatedResponse {
  readonly roleId: string;
  readonly roleKey: string;
  readonly displayName: string;
  readonly permissionKeys: readonly string[];
}

export interface MembershipListResponse {
  readonly memberships: readonly MembershipSummary[];
}

export interface RoleTemplateSummary {
  readonly roleId: string;
  readonly roleKey: string;
  readonly roleVersion: number;
  readonly status: string;
  readonly displayName: string;
  readonly requiresExplicitConfirmation?: boolean;
  /** Present only for member-scoped `custom-*` roles: their ALLOW grants. */
  readonly permissionKeys?: readonly string[];
}

export interface RoleListResponse {
  readonly roles: readonly RoleTemplateSummary[];
}

export interface InvitationCreatedResponse {
  readonly accountId: string;
  readonly membershipId: string;
  readonly inviteId: string;
  readonly expiresAt: string;
  readonly duplicate: boolean;
  readonly memberRef?: string;
  readonly inviteToken?: string;
}

/** First Login Profile. Mirrors packages/auth/src/membership-admin/service.ts's own constants. */
export const AVATAR_PRESET_IDS = [
  'a1',
  'a2',
  'a3',
  'a4',
  'a5',
  'a6',
  'a7',
  'a8',
  'a9',
  'a10',
] as const;
export type AvatarPresetId = (typeof AVATAR_PRESET_IDS)[number];

export const THEME_MODES = ['light', 'dark', 'system'] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

export const BRAND_COLORS = ['indigo', 'emerald', 'ruby', 'amber', 'violet', 'slate'] as const;
export type BrandColor = (typeof BRAND_COLORS)[number];

export const SIDEBAR_SKINS = ['brand', 'brand-deep', 'ink', 'surface'] as const;
export type SidebarSkin = (typeof SIDEBAR_SKINS)[number];

export const AVATAR_MAX_BYTES = 2_000_000;
export const ALLOWED_AVATAR_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export type AvatarMimeType = (typeof ALLOWED_AVATAR_MIME_TYPES)[number];

/** `GET|PATCH t/:slug/api/v1/me` — mirrors `OwnProfile` in
 * packages/auth/src/membership-admin/service.ts, as returned by
 * apps/api/src/membership/membership-admin.controller.ts. */
export interface OwnProfileResponse {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly mobile: string | null;
  readonly email: string | null;
  /** Business date `YYYY-MM-DD` (Gregorian at rest — display only through `lib/date`). */
  readonly birthdate: string | null;
  readonly avatarPreset: AvatarPresetId | null;
  readonly avatarPhotoDataUrl: string | null;
  readonly themeMode: ThemeMode | null;
  readonly brandColor: BrandColor | null;
  readonly sidebarSkin: SidebarSkin | null;
  /** Null until the first-login wizard is finished — the gate AuthenticatedShell checks. */
  readonly profileCompletedAt: string | null;
  /** Only a full tenant admin may self-edit firstName/lastName here — see the controller's own doc comment. */
  readonly canEditLockedFields: boolean;
  readonly membershipStatus: string;
  readonly roleDisplayNames: readonly string[];
  readonly accessReviewRequired?: boolean;
  /**
   * NEW-03 — server-computed DENY-first capability set. Optional on older
   * payloads so a mixed deploy still typechecks; the live API sends it.
   * payloads so a mixed deploy still typechecks; the live API always sends it.
   */
  readonly capabilities?: {
    readonly allowedKeys: readonly string[];
    readonly entitledModuleSlugs: readonly string[];
    readonly capabilityVersion: number;
    readonly roleKeys: readonly string[];
  };
}

/** `PATCH t/:slug/api/v1/me` body — every key optional, `null` (where allowed) clears it. */
export interface OwnProfilePatchBody {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string | null;
  readonly birthdate?: string | null;
  readonly avatar?:
    | { readonly kind: 'photo'; readonly mimeType: AvatarMimeType; readonly base64: string }
    | { readonly kind: 'preset'; readonly preset: AvatarPresetId }
    | { readonly kind: 'none' };
  readonly themeMode?: ThemeMode | null;
  readonly brandColor?: BrandColor | null;
  readonly sidebarSkin?: SidebarSkin | null;
}
